// src/pages/MainPage.js
import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import UserMenu from "../components/UserMenu";
// Import LoginTailwind instead of the original Login component
import { LoginTailwind as Login } from "../components";
import { useAuth } from "../context/AuthContext";
import { useEnvironment } from "../context/EnvironmentContext";
import MemberPage from "./MemberPage"; // Import the dedicated MemberPage component
import {
  TransaksiWithEnv as Transaksi,
  SimpanPinjamWithEnv as SimpanPinjam,
  DaftarAnggotaBaruWithEnv as DaftarAnggotaBaru,
  StocksWithEnv as Stocks,
  WarehouseStockWithEnv as WarehouseStock,
  SejarahBelanjaWithEnv as SejarahBelanja,
  SejarahBelanjaWarehouseWithEnv as SejarahBelanjaWarehouse,
  WarehouseExitWithEnv as WarehouseExit,
  SejarahTransaksiWithEnv as SejarahTransaksi,
  SejarahTransaksiWarehouseWithEnv as SejarahTransaksiWarehouse,
  NotaBelanjaB2BWithEnv as NotaBelanjaB2B,
  AdminPanelWithEnv as AdminPanel,
  FinanceWithEnv as Finance,
  AdminSettings,
} from "../components";
import TailwindTest from "../components/TailwindTest";
import TabunganLogs from "../components/TabunganLogs";
import VoucherKoperasiPageNew from "./VoucherKoperasiPageNew";
import MemberPreviewBanner from "../components/MemberPreviewBanner";

const MainPage = () => {
  const {
    currentUser,
    userRole,
    hasAccess,
    isMemberPreviewing,
    previewMember,
    stopMemberPreview,
  } = useAuth();
  const { isProduction } = useEnvironment();
  const [activeComponent, setActiveComponent] = useState("Transaksi");
  const [isCollapsed, setIsCollapsed] = useState(false);

  // If user doesn't have access to current component, switch to one they can access
  useEffect(() => {
    if (currentUser && !hasAccess(activeComponent)) {
      // Find the first component they have access to
      if (hasAccess("Transaksi")) {
        setActiveComponent("Transaksi");
      } else if (hasAccess("DaftarAnggotaBaru")) {
        setActiveComponent("DaftarAnggotaBaru");
      } else if (hasAccess("Stocks")) {
        setActiveComponent("Stocks");
      } else if (hasAccess("WarehouseStock")) {
        setActiveComponent("WarehouseStock");
      } else if (hasAccess("SimpanPinjam")) {
        setActiveComponent("SimpanPinjam");
      } else if (hasAccess("SejarahTransaksi")) {
        setActiveComponent("SejarahTransaksi");
      } else if (hasAccess("TabunganLogs")) {
        setActiveComponent("TabunganLogs");
      } else if (hasAccess("SejarahBelanja")) {
        setActiveComponent("SejarahBelanja");
      } else if (hasAccess("WarehouseExit")) {
        setActiveComponent("WarehouseExit");
      } else if (hasAccess("SejarahBelanjaWarehouse")) {
        setActiveComponent("SejarahBelanjaWarehouse");
      } else if (hasAccess("SejarahTransaksiWarehouse")) {
        setActiveComponent("SejarahTransaksiWarehouse");
      } else if (hasAccess("NotaBelanjaB2B")) {
        setActiveComponent("NotaBelanjaB2B");
      } else if (hasAccess("Finance")) {
        setActiveComponent("Finance");
      } else if (hasAccess("VoucherKoperasi")) {
        setActiveComponent("VoucherKoperasi");
      } else if (hasAccess("AdminPanel")) {
        setActiveComponent("AdminPanel");
      } else if (hasAccess("MemberPage")) {
        setActiveComponent("MemberPage");
      }
    }
  }, [currentUser, activeComponent, hasAccess]);

  const renderContent = () => {
    // Check if user has access to this component
    if (currentUser && !hasAccess(activeComponent)) {
      return (
        <div className="access-denied">
          <h2>Akses Terbatas</h2>
          <p>Maaf, Anda tidak memiliki akses ke halaman ini.</p>
        </div>
      );
    }

    switch (activeComponent) {
      case "Transaksi":
        return <Transaksi />;
      case "SimpanPinjam":
        return <SimpanPinjam />;
      case "DaftarAnggotaBaru":
        return <DaftarAnggotaBaru setActivePage={setActiveComponent} />;
      case "TabunganLogs":
        return <TabunganLogs />;
      case "Stocks":
        return <Stocks />;
      case "WarehouseStock":
        return <WarehouseStock />;
      case "SejarahBelanja":
        return <SejarahBelanja />;
      case "SejarahBelanjaWarehouse":
        return <SejarahBelanjaWarehouse />;
      case "SejarahTransaksiWarehouse":
        return <SejarahTransaksiWarehouse />;
      case "WarehouseExit":
        return <WarehouseExit />;
      case "NotaBelanjaB2B":
        return <NotaBelanjaB2B />;
      case "SejarahTransaksi":
        return <SejarahTransaksi />;
      case "Finance":
        return <Finance />;
      case "AdminPanel":
        return <AdminPanel />;
      case "AdminSettings":
        return <AdminSettings />;
      case "TailwindTest":
        return <TailwindTest />;
      case "VoucherKoperasi":
        return <VoucherKoperasiPageNew />;
      case "MemberPage":
        return <MemberPage />;
      default:
        return <Transaksi />;
    }
  };

  const handleToggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleExitMemberPreview = () => {
    setActiveComponent("DaftarAnggotaBaru");
    stopMemberPreview();
  };

  // If not logged in, show login page
  if (!currentUser) {
    return <Login />;
  }

  if (isMemberPreviewing && previewMember) {
    return (
      <div className="member-preview-shell">
        <MemberPreviewBanner
          member={previewMember}
          onExit={handleExitMemberPreview}
        />
        <MemberPage
          previewMember={previewMember}
          readOnly
          onExitPreview={handleExitMemberPreview}
        />
      </div>
    );
  }

  // If user is a Member, show the dedicated Member Page without sidebar
  if (userRole === "Member") {
    return <MemberPage />;
  }

  // For all other roles, show the standard admin interface with sidebar
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Header with user menu */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 20px",
          backgroundColor: isProduction ? "#fff" : "#ff9800",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
          zIndex: 10,
          transition: "background-color 0.3s",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "1.2rem",
            color: isProduction ? "#000" : "#fff",
          }}
        >
          Koperasi Unipdu
        </h1>
        {!isProduction && (
          <span
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#fff",
              color: "#e65100",
              padding: "2px 10px",
              borderRadius: "4px",
              fontSize: "0.75rem",
              fontWeight: "bold",
              border: "2px solid #e65100",
              letterSpacing: "0.5px",
            }}
          >
            TESTING
          </span>
        )}
        <div style={{ marginLeft: "auto" }} />
        <UserMenu />
      </header>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Sidebar
          onSelect={setActiveComponent}
          selectedItem={activeComponent}
          isCollapsed={isCollapsed}
          onCollapseToggle={handleToggleSidebar}
        />

        <div
          style={{
            marginLeft: isCollapsed ? "70px" : "250px",
            padding: "20px",
            width: "calc(100% - 70px)",
            maxWidth: isCollapsed ? "calc(100% - 70px)" : "calc(100% - 250px)",
            transition: "all 0.3s ease",
            overflow: "auto",
          }}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default MainPage;
